import React, { useContext, useEffect, useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  FlatList,
} from "react-native";
import { AntDesign, Feather } from "@expo/vector-icons";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllRejectedHistoryChinaOrder,
  getAllChinaOrganizationalUsers,
  getAllChinaTrackerEnquiries,
  getAllFactoryFlags,
  updateChinaChecklist,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import Spinner from "../../../components/shared/Spinner";
import { useToast } from "react-native-toast-notifications";
import 
{
  translateFields,
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";

interface ChecklistItem {
  _id: string;
  key: string;
  orderNumber: boolean;
  message: string;
  assignTo?: string;
  assignedstatus?: "TO-DO" | "IN-PROGRESS" | "DONE" | "REVIEW";
}

interface User {
  _id: string;
  display_name: string;
  first_name: string;
  last_name: string;
  email: string;
  login: string;
}

interface ChecklistModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
}

const ChecklistModal: React.FC<ChecklistModalProps> = ({
  visible,
  onClose,
  title = "Checklist",
}) => {
  const {
    loadingEnquiries,
    enquiries,
    loadingFlag,
    factoryFlags,
    loadingUsers,
    users,
    loadingRejectedHistory,
    rejectedHistory,
  } = useSelector((state: any) => state.chinaTracker);

  const dispatch = useDispatch<any>();
  const toast = useToast();
  const { t } = useTranslation();

  const [expandedSections, setExpandedSections] = useState({
    flagged: false,
    enquiries: false,
  });
  const [progress, setProgress] = useState<number>(0);

  const [expandedTasks, setExpandedTasks] = useState<{
    [key: string]: boolean;
  }>({});

  const [dropdownType, setDropdownType] = useState<{
    [key: string]: "user" | "status" | null;
  }>({});

  const statusOptions = ["TO-DO", "IN-PROGRESS", "DONE", "REVIEW"];

  useEffect(() => {
    dispatch(getAllChinaTrackerEnquiries());
    dispatch(getAllFactoryFlags());
    dispatch(getAllChinaOrganizationalUsers());
    dispatch(fetchAllRejectedHistoryChinaOrder());
  }, []);

  useEffect(() => {
    const completedFlags = factoryFlags.filter(
      (flag: any) => flag?.assignedstatus === "DONE"
    ).length;
    const totalFlags = factoryFlags.length;

    const completedEnquiries = enquiries.filter(
      (flag: any) => flag?.assignedstatus === "DONE"
    ).length;
    const totalEnquiries = enquiries.length;

    const completedRejected = rejectedHistory.filter(
      (flag: any) => flag?.assignedstatus === "DONE"
    ).length;
    const totalRejected = rejectedHistory.length;

    const totalCompleted =
      completedFlags + completedEnquiries + completedRejected;
    const totalItems = totalFlags + totalEnquiries + totalRejected;

    const progress = totalItems === 0 ? 0 : (totalCompleted / totalItems) * 100;

    setProgress(parseFloat(progress.toFixed(2)));
  }, [factoryFlags, enquiries, rejectedHistory]);

  const toggleSection = (section: "flagged" | "enquiries") => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleTask = (taskId: string) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));

    // Close dropdowns when collapsing task
    if (expandedTasks[taskId]) {
      setDropdownType((prev) => ({
        ...prev,
        [taskId]: null,
      }));
    }
  };

  const toggleDropdown = (taskId: string, type: "user" | "status") => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: true,
    }));

    setDropdownType((prev) => ({
      ...prev,
      [taskId]: prev[taskId] === type ? null : type,
    }));
  };

  const handleUserAssignment = (
    taskId: string,
    user: User,
    section: "flagged" | "enquiries" | "rejected"
  ) => {
    const updateData = {
      taskId,
      assignTo: user._id,
      assignedstatus: "IN-PROGRESS",
      section,
    };

    dispatch(updateChinaChecklist(updateData))
      .then((data: any) => {
        toast.show("Successfully assigned the task", {
          type: "success",
        });
      })
      .catch((error: any) => {
        toast.show(error, {
          type: "danger",
        });
      });

    // Close dropdown after selection
    setDropdownType((prev) => ({
      ...prev,
      [taskId]: null,
    }));
  };

  const handleStatusChange = (
    taskId: string,
    status: string,
    section: "flagged" | "enquiries" | "rejected"
  ) => {
    const updateData = {
      taskId,
      assignedstatus: status,
      section,
    };

    dispatch(updateChinaChecklist(updateData))
      .then((data: any) => {
        toast.show("Successfully updated status", {
          type: "success",
        });
      })
      .catch((error: any) => {
        toast.show(error, {
          type: "danger",
        });
      });

    // Close dropdown after selection
    setDropdownType((prev) => ({
      ...prev,
      [taskId]: null,
    }));
  };

  const getUserDisplayName = (userId: string) => {
    const user = users.find((u: User) => u._id === userId);
    return user ? user.display_name : "Unknown User";
  };

  const renderProgressBar = () => (
    <View style={styles.progressContainer}>
      <View style={styles.progressBar}>
        <View style={[styles.progressFill, { width: `${progress}%` }]} />
      </View>
      <Text style={styles.progressText}>{progress}% Complete</Text>
    </View>
  );

  const renderUserDropdown = (
    taskId: string,
    section: "flagged" | "enquiries" | "rejected"
  ) => {
    if (dropdownType[taskId] !== "user") return null;

    return (
      <View style={styles.dropdownContainer}>
        <Text style={styles.dropdownTitle}>Assign to:</Text>
        <FlatList
          data={users}
          keyExtractor={(item) => item._id}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.dropdownItem}
              onPress={() => handleUserAssignment(taskId, item, section)}
            >
              <Text style={styles.dropdownItemText}>{item.display_name}</Text>
              <Text style={styles.dropdownItemSubtext}>{item.email}</Text>
            </TouchableOpacity>
          )}
          style={styles.dropdownList}
          showsVerticalScrollIndicator={false}
        />
      </View>
    );
  };

  const renderStatusDropdown = (
    taskId: string,
    section: "flagged" | "enquiries" | "rejected"
  ) => {
    if (dropdownType[taskId] !== "status") return null;

    return (
      <View style={styles.dropdownContainer}>
        <Text style={styles.dropdownTitle}>Change status:</Text>
        <FlatList
          data={statusOptions}
          keyExtractor={(item) => item}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.dropdownItem}
              onPress={() => handleStatusChange(taskId, item, section)}
            >
              <Text style={styles.dropdownItemText}>{item}</Text>
            </TouchableOpacity>
          )}
          style={styles.dropdownList}
          showsVerticalScrollIndicator={false}
        />
      </View>
    );
  };

  const renderTaskItem = (
    item: ChecklistItem,
    section: "flagged" | "enquiries" | "rejected"
  ) => {
    const isCompleted = item?.assignedstatus === "DONE";
    const isAssigned = item?.assignTo;
    const isExpanded = expandedTasks[item._id];

    return (
      <View key={item._id} style={styles.taskItem}>
        <TouchableOpacity
          style={[styles.taskContent, isExpanded && styles.taskContentExpanded]}
          onPress={() => toggleTask(item._id)}
        >
          <View style={styles.taskLeft}>
            <Text style={styles.taskText}>
              Order Number {item.orderNumber} - {item.message}
            </Text>
          </View>

          <View style={styles.taskRight}>
            {/* Status Badge */}
            {item?.assignedstatus && (
              <TouchableOpacity
                style={[
                  styles.statusBadge,
                  item.assignedstatus === "IN-PROGRESS" &&
                    styles.inProgressBadge,
                  item.assignedstatus === "DONE" && styles.completedBadge,
                  item.assignedstatus === "REVIEW" && styles.reviewBadge,
                ]}
                onPress={() => toggleDropdown(item._id, "status")}
                disabled={isCompleted || !isAssigned}
              >
                <Text
                  style={[
                    styles.statusText,
                    item.assignedstatus === "IN-PROGRESS" &&
                      styles.inProgressText,
                    item.assignedstatus === "DONE" && styles.completedText,
                    item.assignedstatus === "REVIEW" && styles.reviewText,
                  ]}
                >
                  {item.assignedstatus}
                </Text>
                {!isCompleted && (
                  <AntDesign name="down" size={10} color="#666" />
                )}
              </TouchableOpacity>
            )}

            {/* Assign To Button */}
            {!isCompleted && (
              <TouchableOpacity
                style={[styles.assignBadge, isAssigned && styles.assignedBadge]}
                onPress={() => toggleDropdown(item._id, "user")}
                disabled={isCompleted}
              >
                <Text style={styles.assignText}>
                  {isAssigned ? getUserDisplayName(item.assignTo) : "Assign to"}
                </Text>
                <AntDesign name="down" size={10} color="#666" />
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={styles.arrowButton}
              onPress={() => toggleTask(item._id)}
            >
              <AntDesign
                name={isExpanded ? "up" : "down"}
                size={16}
                color="#666"
              />
            </TouchableOpacity>
          </View>
        </TouchableOpacity>

        {/* Expanded Content */}
        {isExpanded && (
          <View style={styles.expandedContent}>
            {/* Additional task details can go here */}
            <View style={styles.taskDetails}>
              <Text style={styles.taskDetailsText}>Task ID: {item._id}</Text>
              {item.assignTo && (
                <Text style={styles.taskDetailsText}>
                  Assigned to: {getUserDisplayName(item.assignTo)}
                </Text>
              )}
              <Text style={styles.taskDetailsText}>
                Status: {item.assignedstatus || "Not set"}
              </Text>
            </View>

            {/* Inline Dropdowns */}
            {renderUserDropdown(item._id, section)}
            {renderStatusDropdown(item._id, section)}
          </View>
        )}
      </View>
    );
  };

  const renderSection = (
    title: string,
    items: ChecklistItem[],
    section: "flagged" | "enquiries" | "rejected",
    count: number,
    color: string
  ) => (
    <View style={styles.section}>
      <TouchableOpacity
        style={styles.sectionHeader}
        onPress={() => toggleSection(section)}
      >
        <Text style={styles.sectionTitle}>{t(section)}</Text>
        <View style={styles.sectionRight}>
          <View style={[styles.countBadge, { borderColor: color }]}>
            <View style={[styles.countDot, { backgroundColor: color }]} />
            <Text style={styles.countText}>{count} Task</Text>
            <AntDesign
              name={expandedSections[section] ? "up" : "down"}
              size={12}
              color="#666"
            />
          </View>
        </View>
      </TouchableOpacity>

      {expandedSections[section] && (
        <View style={styles.sectionContent}>
          {items.map((item) => renderTaskItem(item, section))}
        </View>
      )}
    </View>
  );

  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );

  const getModalWidth = () => {
    if (isSmallDevice) return "90%";
    if (isMediumDevice) return "80%";
    return "76%";
  };

  const getInnerContainerWidth = () => {
    if (isSmallDevice) return "100%";
    return "100%";
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={[styles.modalContainer, { width: getModalWidth() }]}>
          {loadingEnquiries ||
          loadingFlag ||
          loadingUsers ||
          loadingRejectedHistory ? (
            <Spinner
              size="small"
              color="#74BBFF"
              styles={{
                flexDirection: "row",
                justifySelf: "center",
                alignSelf: "center",
              }}
            />
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                { width: getInnerContainerWidth() },
              ]}
            >
              <View style={styles.modalContent}>
                {/* Header */}
                <View style={styles.header}>
                  <Text style={styles.title}>{t("checklist")}</Text>
                </View>

                {/* Progress Bar */}
                {renderProgressBar()}

                {/* Content */}
                <ScrollView
                  style={styles.scrollContent}
                  showsVerticalScrollIndicator={false}
                >
                  {renderSection(
                    "Flagged",
                    factoryFlags,
                    "flagged",
                    factoryFlags?.length,
                    "#22C55E"
                  )}
                  {renderSection(
                    "Enquiries",
                    enquiries,
                    "enquiries",
                    enquiries?.length,
                    "#22C55E"
                  )}
                  {renderSection(
                    "Rejected",
                    rejectedHistory,
                    "rejected",
                    rejectedHistory?.length,
                    "#22C55E"
                  )}
                </ScrollView>

                {/* Footer */}
                <View style={styles.footer}>
                  <TouchableOpacity
                    style={styles.closeButton}
                    onPress={onClose}
                  >
                    <Text style={styles.closeButtonText}>{t("close")}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  modalContainer: {
    backgroundColor: "#fafafa",
    padding: 25,
    elevation: 8,
    maxHeight: "90%",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 8,
    height: "100%",
  },
  modalContent: {
    flex: 1,
  },
  header: {
    paddingVertical: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e5e5",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#333",
  },
  progressContainer: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  progressBar: {
    height: 8,
    backgroundColor: "#e5e5e5",
    borderRadius: 4,
    marginBottom: 8,
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#22C55E",
    borderRadius: 4,
  },
  progressText: {
    fontSize: 14,
    color: "#666",
    fontWeight: "500",
  },
  scrollContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  section: {
    marginBottom: 20,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e5e5",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333",
  },
  sectionRight: {
    flexDirection: "row",
    alignItems: "center",
  },
  countBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    backgroundColor: "#fff",
  },
  countDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  countText: {
    fontSize: 12,
    color: "#666",
    marginRight: 4,
  },
  sectionContent: {
    marginTop: 8,
  },
  taskItem: {
    marginBottom: 8,
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e5e5",
    overflow: "hidden",
  },
  taskContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  taskContentExpanded: {
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  taskLeft: {
    flex: 1,
    marginRight: 12,
  },
  taskText: {
    fontSize: 14,
    color: "#333",
  },
  taskRight: {
    flexDirection: "row",
    alignItems: "center",
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: "#f3f4f6",
    borderRadius: 12,
    marginRight: 8,
  },
  inProgressBadge: {
    backgroundColor: "#dbeafe",
  },
  completedBadge: {
    backgroundColor: "#dcfce7",
  },
  reviewBadge: {
    backgroundColor: "#fef3c7",
  },
  statusText: {
    fontSize: 12,
    color: "#666",
    marginRight: 4,
  },
  inProgressText: {
    color: "#1e40af",
  },
  completedText: {
    color: "#166534",
  },
  reviewText: {
    color: "#d97706",
  },
  assignBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: "#f3f4f6",
    borderRadius: 12,
    marginRight: 8,
  },
  assignedBadge: {
    backgroundColor: "#e0f2fe",
  },
  assignText: {
    fontSize: 12,
    color: "#666",
    marginRight: 4,
  },
  arrowButton: {
    padding: 4,
  },
  expandedContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    backgroundColor: "#f8f9fa",
  },
  taskDetails: {
    marginBottom: 12,
    paddingVertical: 8,
  },
  taskDetailsText: {
    fontSize: 12,
    color: "#666",
    marginBottom: 4,
  },
  footer: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#e5e5e5",
  },
  closeButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: "#f3f4f6",
    borderRadius: 8,
    alignItems: "center",
  },
  closeButtonText: {
    fontSize: 16,
    color: "#374151",
    fontWeight: "500",
  },
  dropdownContainer: {
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e5e5",
    marginTop: 8,
    maxHeight: 200,
  },
  dropdownTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  dropdownList: {
    maxHeight: 150,
  },
  dropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  dropdownItemText: {
    fontSize: 14,
    color: "#333",
    fontWeight: "500",
  },
  dropdownItemSubtext: {
    fontSize: 12,
    color: "#666",
    marginTop: 2,
  },
});

export default ChecklistModal;
